import { useEffect, useState } from 'react';
import { LogOut, Plus, RotateCcw } from 'lucide-react';
import { Avatar } from '@/components/ui/Avatar';
import { Tag } from '@/components/ui/badges';
import { fmtMoneyExact } from '@/lib/domain/format';
import { type Investment, ROUND_TYPE_LABEL, fmtIrr, fmtMoic, toInvestment } from './types';
import { TimelineSection } from '@/features/timeline/TimelineSection';
import { useInvestment } from './useInvestment';
import { InvestmentEditForm } from './InvestmentEditForm';
import { FollowOnModal } from './FollowOnModal';
import { DivestModal } from './DivestModal';
import { ReactivateModal } from './ReactivateModal';

type ActionModal = 'followon' | 'divest' | 'reactivate' | null;

function fmtRoundDate(iso: string): string {
  return new Date(iso).toLocaleDateString('pt-BR', { timeZone: 'UTC', day: '2-digit', month: 'short', year: 'numeric' });
}

/**
 * Ficha do Investimento (Portfólio v2 — DEC-040). Read-only por padrão; "Editar" abre o formulário
 * (UpdateInvestment). Lista as rodadas de aporte; "+ Aporte" abre o follow-on. Quando ativo,
 * "Desinvestir" abre o modal rigoroso (métricas + comentário); quando desinvestido, "Reativar".
 * A ficha é dirigida pelo GET /api/investments/{id} (refetch após cada mutação reflete na hora).
 */
export function InvestmentDrawer({ inv, onClose }: { inv: Investment; onClose: () => void }) {
  const detail = useInvestment(inv.id);
  const [editing, setEditing] = useState(false);
  const [modal, setModal] = useState<ActionModal>(null);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape' && modal === null && !editing) onClose();
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose, modal, editing]);

  // Fonte de verdade: a ficha fresca (detail). Antes de carregar, pinta com o item da tabela.
  const view = detail.data ? toInvestment(detail.data) : inv;
  const team = detail.data?.teamUserNames ?? [];
  const tags = detail.data?.tags ?? [];
  const rounds = detail.data?.rounds ?? [];
  const ready = detail.data != null;
  const etag = view.etag;

  const metrics: [string, string][] = [
    ['MOIC esp.', fmtMoic(view.moicExpected)],
    ['MOIC real.', fmtMoic(view.moicRealized)],
    ['TIR esp.', fmtIrr(view.irrExpected)],
    ['TIR real.', fmtIrr(view.irrRealized)],
  ];

  return (
    <>
      <button
        type="button"
        aria-label="Fechar ficha"
        onClick={onClose}
        className="fixed inset-0 z-[70] cursor-default bg-[rgba(20,18,12,0.45)]"
      />
      <div
        role="dialog"
        aria-label={`Ficha do investimento ${view.codename}`}
        className="fixed left-1/2 top-1/2 z-[80] max-h-[90vh] w-[min(620px,94vw)] -translate-x-1/2 -translate-y-1/2 overflow-y-auto rounded-lg bg-vega-surface p-6 shadow-vega-lg"
      >
        <div className="mb-1 flex items-baseline gap-3">
          <h2 className="text-[18px] font-semibold tracking-tight text-vega-ink">{view.codename}</h2>
          <span className="text-[12px] text-vega-muted">Investimento</span>
        </div>

        <div className="mb-4 mt-4 grid grid-cols-3 gap-3.5">
          <Field label="Empresa">
            <span className="text-[13px] font-medium text-vega-ink">{view.companyName}</span>
          </Field>
          <Field label="Fundo">
            <Tag>{view.fundName}</Tag>
          </Field>
          <Field label="Status">
            <Tag tone={view.status === 'Active' ? 'accent' : 'default'}>
              {view.status === 'Active' ? 'Ativo' : 'Desinvestido'}
            </Tag>
          </Field>
        </div>

        {editing && detail.data ? (
          <InvestmentEditForm detail={detail.data} onDone={() => setEditing(false)} />
        ) : (
          <>
            <div className="mb-3.5 rounded border border-vega-line-2 bg-vega-surface-2 px-4 py-3">
              <div className="mb-1 text-[10.5px] uppercase tracking-wide text-vega-muted">
                Cheque{rounds.length > 0 ? ` · ${rounds.length} ${rounds.length === 1 ? 'rodada' : 'rodadas'}` : ''}
              </div>
              <div className="font-mono text-[22px] font-semibold tracking-tight text-vega-ink">
                {fmtMoneyExact(view.checkAmountBRL) || '—'}
              </div>
              {detail.data && (detail.data.checkDiscretionaryBRL != null || detail.data.checkCoinvestmentBRL != null) && (
                <div className="mt-1.5 text-[11.5px] text-vega-muted">
                  Discricionário {fmtMoneyExact(detail.data.checkDiscretionaryBRL) || '—'}
                  {' · '}
                  Coinvestimento {fmtMoneyExact(detail.data.checkCoinvestmentBRL) || '—'}
                </div>
              )}
            </div>

            <div className="grid grid-cols-4 gap-px overflow-hidden rounded border border-vega-line-2 bg-vega-line-2">
              {metrics.map(([label, value]) => (
                <div key={label} className="bg-vega-surface px-3 py-2.5">
                  <div className="mb-1 text-[10px] uppercase tracking-wide text-vega-muted">{label}</div>
                  <div className="font-mono text-[16px] font-semibold text-vega-ink">{value}</div>
                </div>
              ))}
            </div>

            <div className="mt-4 grid grid-cols-[120px_1fr] gap-3 text-[12.5px]">
              <div className="pt-0.5 text-[11px] uppercase tracking-wide text-vega-muted">Tese</div>
              <div className="text-vega-ink">{view.thesisName ? <Tag size="sm">{view.thesisName}</Tag> : '—'}</div>
              <div className="pt-0.5 text-[11px] uppercase tracking-wide text-vega-muted">Responsável</div>
              <div className="inline-flex items-center gap-2 text-vega-ink">
                <Avatar name={view.leadUserName} colorKey={view.leadUserId} size={18} /> {view.leadUserName}
              </div>
              {team.length > 0 && (
                <>
                  <div className="pt-0.5 text-[11px] uppercase tracking-wide text-vega-muted">Equipe</div>
                  <div className="text-vega-muted">{team.join(', ')}</div>
                </>
              )}
              {tags.length > 0 && (
                <>
                  <div className="pt-0.5 text-[11px] uppercase tracking-wide text-vega-muted">Tags</div>
                  <div className="inline-flex flex-wrap gap-1">
                    {tags.map((t) => (
                      <Tag key={t} size="sm">
                        {t}
                      </Tag>
                    ))}
                  </div>
                </>
              )}
            </div>

            {rounds.length > 0 && (
              <div className="mt-4">
                <div className="mb-1.5 text-[11px] uppercase tracking-wide text-vega-muted">Rodadas de aporte</div>
                <div className="overflow-hidden rounded border border-vega-line-2">
                  {rounds.map((r, i) => (
                    <div
                      key={r.id}
                      className={`flex items-center justify-between px-3 py-2 text-[12.5px] ${
                        i > 0 ? 'border-t border-vega-line-2' : ''
                      }`}
                    >
                      <span className="inline-flex items-center gap-2">
                        <Tag size="sm" tone={r.roundType === 'Initial' ? 'accent' : 'default'}>
                          {ROUND_TYPE_LABEL[r.roundType] ?? r.roundType}
                        </Tag>
                        <span className="text-vega-muted">{fmtRoundDate(r.deployedAtUtc)}</span>
                      </span>
                      <span className="font-mono font-semibold text-vega-ink">{fmtMoneyExact(r.amountBRL)}</span>
                    </div>
                  ))}
                </div>
              </div>
            )}

            <div className="mt-5">
              <TimelineSection linkType="Investment" linkId={view.id} />
            </div>

            <div className="mt-5 flex flex-wrap items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="rounded border border-vega-line px-3 py-1.5 text-[12.5px] font-medium text-vega-muted"
              >
                Fechar
              </button>
              <div className="flex-1" />
              {view.status === 'Active' && (
                <button
                  type="button"
                  onClick={() => setModal('followon')}
                  disabled={!ready}
                  className="inline-flex items-center gap-1.5 rounded border border-vega-line px-3 py-1.5 text-[12.5px] font-medium text-vega-ink-2 disabled:opacity-50"
                >
                  <Plus size={13} /> Aporte
                </button>
              )}
              {view.status === 'Active' ? (
                <button
                  type="button"
                  onClick={() => setModal('divest')}
                  disabled={!ready}
                  className="inline-flex items-center gap-1.5 rounded border border-vega-line px-3 py-1.5 text-[12.5px] font-medium text-vega-ink-2 disabled:opacity-50"
                >
                  <LogOut size={13} /> Desinvestir
                </button>
              ) : (
                <button
                  type="button"
                  onClick={() => setModal('reactivate')}
                  disabled={!ready}
                  className="inline-flex items-center gap-1.5 rounded border border-vega-line px-3 py-1.5 text-[12.5px] font-medium text-vega-ink-2 disabled:opacity-50"
                >
                  <RotateCcw size={13} /> Reativar
                </button>
              )}
              <button
                type="button"
                onClick={() => setEditing(true)}
                disabled={!ready}
                className="rounded border border-vega-line px-3 py-1.5 text-[12.5px] font-medium text-vega-ink-2 disabled:opacity-50"
              >
                Editar
              </button>
            </div>
          </>
        )}
      </div>

      {modal === 'followon' && (
        <FollowOnModal id={view.id} etag={etag} codename={view.codename} onClose={() => setModal(null)} />
      )}
      {modal === 'divest' && (
        <DivestModal id={view.id} etag={etag} codename={view.codename} onClose={() => setModal(null)} />
      )}
      {modal === 'reactivate' && (
        <ReactivateModal id={view.id} etag={etag} codename={view.codename} onClose={() => setModal(null)} />
      )}
    </>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div>
      <div className="text-[10.5px] uppercase tracking-wide text-vega-muted">{label}</div>
      <div className="mt-1">{children}</div>
    </div>
  );
}
